import React, { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { DndContext } from '@dnd-kit/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import WeekSchedulerView from './WeekSchedulerView';
import DaySchedulerView from './DaySchedulerView';
import MonthSchedulerView from './MonthSchedulerView';
import TechWeekView from './TechWeekView';
import CalendarMobileList from './CalendarMobileList';
import ScheduleJobModal from '../scheduler/ScheduleJobModal';
import DateNavigator from '../DateNavigator';
import { useDateUrl } from '../../hooks/useDateUrl';

import { getDisplayName } from '../../lib/utils';

// For Week view, anchor the 7-day window to the chosen day (typically today), not Sunday.
const getStartOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setHours(0, 0, 0, 0);
  return d;
};

const SchedulerShell = () => {
  const [view, setView] = useState('week'); // 'day' | 'week' | 'month' | 'team'
  // URL deep-link: ?date=YYYY-MM-DD seeds the anchor and stays in sync
  // as the operator navigates (DateNavigator pick, Today, prev/next).
  const { initialDate: urlDate, urlDateYmd, syncDate } = useDateUrl();
  const [anchorDate, setAnchorDate] = useState(() => urlDate || new Date());
  // Pull: react to Back/Forward.
  useEffect(() => {
    if (!urlDateYmd) return;
    const y = anchorDate.getFullYear();
    const m = String(anchorDate.getMonth() + 1).padStart(2, '0');
    const d = String(anchorDate.getDate()).padStart(2, '0');
    if (`${y}-${m}-${d}` === urlDateYmd) return;
    const [yy, mm, dd] = urlDateYmd.split('-').map(Number);
    setAnchorDate(new Date(yy, mm - 1, dd, 0, 0, 0, 0));
  }, [urlDateYmd]);
  // Push: react to user-driven state changes.
  useEffect(() => { syncDate(anchorDate); }, [anchorDate, syncDate]);
  const [techFilter, setTechFilter] = useState(null); // null = all, uid string, or 'unassigned'
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const { companyId } = useCompany();
  const { tasks, addTask, updateTask, rescheduleTask, ensureTaskWindow } = useTasks();

  // Expand the bounded task window when navigating outside the default range
  useEffect(() => {
    const start = new Date(anchorDate); start.setDate(start.getDate() - 45);
    const end = new Date(anchorDate); end.setDate(end.getDate() + 45);
    ensureTaskWindow(start, end);
  }, [anchorDate, ensureTaskWindow]);
  const { leads, updateLead, ensureLeadIds } = useLeads();

  // Backfill leads referenced by visible tasks but outside the leads window
  useEffect(() => {
    const known = new Set(leads.map((l) => l.id));
    const missing = [...new Set(tasks.map((t) => t.leadId).filter(Boolean))].filter((id) => !known.has(id));
    if (missing.length) ensureLeadIds(missing);
  }, [tasks, leads, ensureLeadIds]);
  const { isTechnician } = useRole();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Pre-selected lead from "Schedule Job" button on lead/client profile
  const [scheduleModalLead, setScheduleModalLead] = useState(null);

  // Technicians: auto-lock the filter to their own UID so they only ever see
  // their assigned jobs. This cannot be overridden by the filter chips UI.
  useEffect(() => {
    if (isTechnician && user?.uid) {
      setTechFilter(user.uid);
    }
  }, [isTechnician, user?.uid]);

  useEffect(() => {
    const preSelectId = location.state?.scheduleLeadId;
    if (!preSelectId || leads.length === 0) return;
    const lead = leads.find((l) => l.id === preSelectId);
    if (!lead) return;
    setScheduleModalLead(lead);
  }, [location.state?.scheduleLeadId, leads]);

  // Track viewport so we can swap in the mobile list view below lg breakpoint.
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Mobile: simplified list-based schedule. The DnD grid is desktop-only by design.
  if (isMobile) return <CalendarMobileList />;

  const weekStart = useMemo(() => getStartOfWeek(anchorDate), [anchorDate]);

  // ── Technician filter: derive unique techs from this week's tasks ──────────
  const weekTechs = useMemo(() => {
    if (!tasks) return [];
    const weekEnd = new Date(weekStart.getTime() + 7 * 86400000);
    const map = new Map();
    tasks.forEach((t) => {
      if (!t.scheduledStart || !t.assignedTo) return;
      const start = new Date(t.scheduledStart);
      if (start >= weekStart && start < weekEnd) {
        if (!map.has(t.assignedTo)) {
          map.set(t.assignedTo, t.assignedToName || t.assignedTo);
        }
      }
    });
    return [...map.entries()].map(([uid, name]) => ({ uid, name }));
  }, [tasks, weekStart]);

  const hasUnassignedThisWeek = useMemo(() => {
    if (!tasks) return false;
    const weekEnd = new Date(weekStart.getTime() + 7 * 86400000);
    return tasks.some((t) => {
      if (!t.scheduledStart || t.assignedTo) return false;
      const start = new Date(t.scheduledStart);
      return start >= weekStart && start < weekEnd;
    });
  }, [tasks, weekStart]);

  const handleToday = () => setAnchorDate(new Date());

  const handlePrev = () => {
    if (view === 'month') {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, prev.getDate()));
    } else if (view === 'week' || view === 'team') {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 7));
    } else {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1));
    }
  };

  const handleNext = () => {
    if (view === 'month') {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, prev.getDate()));
    } else if (view === 'week' || view === 'team') {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 7));
    } else {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1));
    }
  };

  const renderView = () => {
    if (view === 'team') {
      return <TechWeekView weekStart={weekStart} />;
    }
    if (view === 'day') {
      return <DaySchedulerView anchorDate={anchorDate} techFilter={techFilter} hideUnscheduled={isTechnician} />;
    }
    if (view === 'month') {
      return <MonthSchedulerView anchorDate={anchorDate} setAnchorDate={setAnchorDate} setView={setView} />;
    }
    return <WeekSchedulerView weekStart={weekStart} techFilter={techFilter} hideUnscheduled={isTechnician} />;
  };

  const currentLabel = useMemo(() => {
    if (view === 'month') {
      return anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    if (view === 'day') {
      return anchorDate.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    const end = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
    const startMonth = weekStart.toLocaleDateString(undefined, { month: 'short' });
    const startDay = weekStart.getDate();
    const endMonth = end.toLocaleDateString(undefined, { month: 'short' });
    const endDay = end.getDate();
    const endYear = end.getFullYear();
    const enDash = '–';
    if (startMonth === endMonth) {
      return `Week of ${startMonth} ${startDay}${enDash}${endDay}, ${endYear}`;
    }
    return `Week of ${startMonth} ${startDay}${enDash}${endMonth} ${endDay}, ${endYear}`;
  }, [anchorDate, view, weekStart]);

  return (
    <DndContext
      onDragEnd={(event) => {
        // Technicians are read-only observers — no drag operations allowed
        if (isTechnician) return;

        const { active, over } = event;
        if (!active || !over) return;

        const activeType = active.data?.current?.type;
        const overType = over.data?.current?.type;

        // Dragging unscheduled lead onto a specific 30-min slot
        // Rule: A lead may have only one active scheduled task
        if (activeType === 'lead' && overType === 'time-slot') {
          const leadId = active.data.current.leadId;
          const { date, slotIdx } = over.data.current;

          if (!leadId || !date || typeof slotIdx !== 'number') return;

          const startMinutes = slotIdx * 30;
          const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
          start.setMinutes(start.getMinutes() + startMinutes);

          const end = new Date(start.getTime() + 60 * 60 * 1000); // 60 minutes default

          const lead = leads.find((l) => l.id === leadId);

          // Check if lead already has an existing job task
          const existingJob = tasks.find(
            (t) => t.leadId === leadId && t.type === 'job'
          );

          if (existingJob) {
            // Update existing task instead of creating duplicate
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[DnD] Updating existing job', existingJob.id, 'for lead', leadId, 'to', start.toISOString());
            }

            if (existingJob.scheduledStart) {
              // Existing job already had a scheduled date — this is a reschedule.
              // Route through the canonical helper so history[] + recurrence
              // cursor stay coherent.
              rescheduleTask(existingJob.id, {
                newScheduledStart: start.toISOString(),
                newScheduledEnd:   end.toISOString(),
                newStatus:         'scheduled',
                reason:            'office',
              }).catch((err) => console.error('[DnD] rescheduleTask failed', err));
            } else {
              // First-time scheduling — no prior state to record in history.
              updateTask(existingJob.id, {
                scheduledStart: start.toISOString(),
                scheduledEnd: end.toISOString(),
                status: 'scheduled',
              });
            }
          } else {
            // First drop: Create new job task
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[DnD] Creating job for lead', leadId, 'at', start.toISOString(), '->', end.toISOString());
            }

            addTask({
              leadId,
              title: getDisplayName(lead, '') || lead?.service || 'Job',
              clientName: getDisplayName(lead, '') || null,
              serviceType: lead?.service || null,
              type: 'job',
              status: 'scheduled',
              scheduledStart: start.toISOString(),
              scheduledEnd: end.toISOString(),
            });
          }

          // Advance lead stage to 'Booked' — confirms the job is on the calendar.
          // Approved = quote accepted (pre-scheduling); Booked = job confirmed on calendar.
          if (leadId) {
            updateLead(leadId, { stage: 'Booked' });
          }
        }

        // Dragging existing job to a new 30-min slot to reschedule
        if (activeType === 'job' && overType === 'time-slot') {
          const { taskId, scheduledStart, scheduledEnd } = active.data.current;
          const { date, slotIdx } = over.data.current;

          const task = tasks.find((t) => t.id === taskId);
          if (!task || !date || typeof slotIdx !== 'number') return;

          const originalStart = scheduledStart ? new Date(scheduledStart) : null;
          const originalEnd = scheduledEnd ? new Date(scheduledEnd) : null;

          const defaultDurationMs = 60 * 60 * 1000;
          const durationMs =
            originalStart && originalEnd && !Number.isNaN(originalStart.getTime()) && !Number.isNaN(originalEnd.getTime())
              ? Math.max(defaultDurationMs, originalEnd.getTime() - originalStart.getTime())
              : defaultDurationMs;

          const startMinutes = slotIdx * 30;
          const newStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
          newStart.setMinutes(newStart.getMinutes() + startMinutes);
          const newEnd = new Date(newStart.getTime() + durationMs);

          if (process.env.NODE_ENV !== 'production') {
            console.debug('[DnD] Rescheduling job', taskId, 'to', newStart.toISOString(), '->', newEnd.toISOString());
          }

          // Canonical reschedule path — writes history[] and syncs the
          // recurrence cursor atomically. Replaces the old updateTask +
          // applyRollingRecurrence pair.
          rescheduleTask(taskId, {
            newScheduledStart: newStart.toISOString(),
            newScheduledEnd:   newEnd.toISOString(),
            reason:            'office',
          }).catch((err) => console.error('[DnD] rescheduleTask failed', err));
        }

        // Dragging job back to staging column: clear schedule and revert lead to Approved
        if (activeType === 'job' && overType === 'staging') {
          const taskId = active.data.current.taskId;
          const task = tasks.find((t) => t.id === taskId);
          if (!task) return;

          if (process.env.NODE_ENV !== 'production') {
            console.debug('[DnD] Unscheduling job', taskId, 'for lead', task.leadId);
          }

          // Canonical unschedule path — appends history (type='unscheduled')
          // and clears the recurrence cursor on recurring leads.
          rescheduleTask(taskId, {
            newScheduledStart: null,
            newScheduledEnd:   null,
            newStatus:         'approved',
            reason:            'office',
          }).catch((err) => console.error('[DnD] unschedule failed', err));

          // Revert lead stage to Approved — job is off the calendar, awaiting rescheduling
          if (task.leadId) {
            updateLead(task.leadId, { stage: 'Approved' });
          }
        }
      }}
    >
      <section className="p-4 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-secondary mb-1">Schedule</h1>
            <p className="text-muted-foreground text-sm">Drag-and-drop calendar for jobs and tasks</p>
            <DateNavigator
              value={anchorDate}
              onChange={setAnchorDate}
              label={currentLabel}
              testId="calendar-date-picker-trigger"
              variant="subtle"
              align="start"
              className="text-xs text-gray-500 mt-1"
            />
          </div>
          <div className="flex items-center gap-4">
            {/* Navigation controls */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="h-8 px-3 text-sm font-medium border border-gray-200 rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
                onClick={handleToday}
              >
                Today
              </button>
              <button
                type="button"
                className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
                onClick={handlePrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
                onClick={handleNext}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {/* View toggle — technicians only get Day/Week (no Team view, no DnD) */}
            <div className="flex items-center bg-gray-100 border border-gray-200 rounded-md p-0.5">
              {['day', 'week', 'month'].map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`view-toggle-${v}`}
                  className={`px-3 py-1.5 text-xs rounded transition-colors ${
                    view === v
                      ? 'bg-white text-gray-900 font-medium shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setView(v)}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
              {/* Team view — admin/dispatcher only */}
              {!isTechnician && (
                <button
                  type="button"
                  data-testid="view-toggle-team"
                  className={`px-3 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
                    view === 'team'
                      ? 'bg-white text-gray-900 font-medium shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setView('team')}
                >
                  <Users className="h-3 w-3" />
                  Team
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Technician filter chips — hidden for technicians (auto-locked to their UID) */}
        {!isTechnician && view !== 'month' && view !== 'team' && (weekTechs.length > 0 || hasUnassignedThisWeek) && (
          <div
            className="flex items-center gap-1.5 flex-wrap"
            data-testid="tech-filter-bar"
          >
            <span className="text-[11px] text-gray-400 font-medium mr-1">Filter:</span>

            {/* All pill */}
            <button
              type="button"
              data-testid="tech-filter-all"
              onClick={() => setTechFilter(null)}
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                techFilter === null
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              All
            </button>

            {/* Per-tech pills */}
            {weekTechs.map(({ uid, name }) => (
              <button
                key={uid}
                type="button"
                data-testid={`tech-filter-${uid}`}
                onClick={() => setTechFilter(techFilter === uid ? null : uid)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  techFilter === uid
                    ? 'bg-[var(--cb-orange)] text-white border-[var(--cb-orange)]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {name}
              </button>
            ))}

            {/* Unassigned pill */}
            {hasUnassignedThisWeek && (
              <button
                type="button"
                data-testid="tech-filter-unassigned"
                onClick={() => setTechFilter(techFilter === 'unassigned' ? null : 'unassigned')}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  techFilter === 'unassigned'
                    ? 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                }`}
              >
                Unassigned
              </button>
            )}
          </div>
        )}

        {renderView()}
      </section>

      {/* Schedule Job Modal — triggered by "Schedule Job" button on lead/client profiles */}
      <ScheduleJobModal
        lead={scheduleModalLead}
        isOpen={!!scheduleModalLead}
        onClose={() => {
          setScheduleModalLead(null);
          // Clear the location state so the modal doesn't reopen on back-navigation
          if (location.state?.scheduleLeadId) {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }}
      />
    </DndContext>
  );
};

export default SchedulerShell;
